import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { PageHeader } from '../components/layout/PageHeader';
import { PageLoader } from '../components/shared/LoadingSpinner';
import { StatusBadge } from '../components/shared/StatusBadge';
import { EmptyState } from '../components/shared/EmptyState';
import { controlsAPI, frameworksAPI } from '../lib/api';
import { truncate } from '../lib/utils';
import { Shield, Search, Filter, ChevronDown, ChevronRight, FileCheck } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../components/ui/collapsible';
import { toast } from 'sonner';

export default function ControlsPage() {
  const [controls, setControls] = useState([]);
  const [frameworks, setFrameworks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFramework, setSelectedFramework] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [expandedCategories, setExpandedCategories] = useState({});
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedFramework]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [controlsRes, frameworksRes] = await Promise.all([
        controlsAPI.getAll(selectedFramework === 'all' ? undefined : selectedFramework),
        frameworksAPI.getAll(),
      ]);
      setControls(controlsRes.data);
      setFrameworks(frameworksRes.data);
      
      // Auto-expand all categories initially
      const categories = [...new Set(controlsRes.data.map(c => c.category || 'Uncategorized'))];
      const expanded = {};
      categories.forEach(cat => { expanded[cat] = true; });
      setExpandedCategories(expanded);
    } catch (error) {
      toast.error('Failed to load controls');
    } finally {
      setLoading(false);
    }
  };

  const handleSeedSOC2 = async () => {
    setSeeding(true);
    try {
      const response = await frameworksAPI.seedSOC2();
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to seed SOC 2 framework');
    } finally {
      setSeeding(false);
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await controlsAPI.updateStatus(id, status);
      toast.success('Control status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  const filteredControls = controls.filter(control => {
    const matchesSearch = 
      control.control_code.toLowerCase().includes(searchQuery.toLowerCase()) ||
      control.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || control.compliance_status === filterStatus;
    return matchesSearch && matchesStatus;
  });

  // Group controls by category
  const groupedControls = filteredControls.reduce((acc, control) => {
    const category = control.category || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(control);
    return acc;
  }, {});

  // Calculate stats
  const stats = {
    total: controls.length,
    compliant: controls.filter(c => c.compliance_status === 'compliant').length,
    pending: controls.filter(c => c.compliance_status === 'pending').length,
    gap: controls.filter(c => c.compliance_status === 'gap').length,
  };
  const complianceRate = stats.total > 0 ? Math.round((stats.compliant / stats.total) * 100) : 0;

  if (loading) {
    return (
      <DashboardLayout>
        <PageLoader />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <PageHeader 
        title="Controls" 
        description="Monitor and manage compliance controls"
        actions={
          controls.length === 0 && (
            <Button
              data-testid="seed-soc2-controls-btn"
              onClick={handleSeedSOC2}
              disabled={seeding}
              className="bg-blue-600 hover:bg-blue-700 rounded-sm"
            >
              {seeding ? 'Loading...' : 'Load SOC 2 Controls'}
            </Button>
          )
        }
      />

      <div className="max-w-7xl mx-auto px-6 py-8 md:px-8 lg:px-12">
        {/* Stats Bar */}
        <div className="bg-white border border-slate-200 rounded-sm p-4 mb-6">
          <div className="flex flex-wrap items-center gap-6">
            <div>
              <p className="text-sm font-mono text-slate-500 uppercase tracking-wider">Compliance Rate</p>
              <p className="text-3xl font-bold text-slate-900">{complianceRate}%</p>
            </div>
            <div className="h-12 w-px bg-slate-200" />
            <div className="flex gap-6">
              <div className="text-center">
                <p className="text-2xl font-bold text-emerald-600">{stats.compliant}</p>
                <p className="text-xs text-slate-500">Compliant</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-slate-500">{stats.pending}</p>
                <p className="text-xs text-slate-500">Pending</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">{stats.gap}</p>
                <p className="text-xs text-slate-500">Gap</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="controls-search"
              placeholder="Search controls..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 h-10 rounded-sm"
            />
          </div>
          <Select value={selectedFramework} onValueChange={setSelectedFramework}>
            <SelectTrigger data-testid="framework-filter" className="w-48 rounded-sm">
              <SelectValue placeholder="Framework" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Frameworks</SelectItem>
              {frameworks.map((fw) => (
                <SelectItem key={fw.id} value={fw.id}>{fw.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger data-testid="status-filter" className="w-40 rounded-sm">
              <Filter size={16} className="mr-2 text-slate-400" />
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="compliant">Compliant</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="gap">Gap</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Controls List */}
        {Object.keys(groupedControls).length > 0 ? (
          <div className="space-y-4">
            {Object.entries(groupedControls).map(([category, categoryControls]) => (
              <Collapsible 
                key={category}
                open={expandedCategories[category]}
                onOpenChange={() => toggleCategory(category)}
              >
                <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
                  <CollapsibleTrigger asChild>
                    <button 
                      className="w-full flex items-center justify-between p-4 bg-slate-50 hover:bg-slate-100 transition-colors"
                      data-testid={`category-${category}`}
                    >
                      <div className="flex items-center gap-3">
                        {expandedCategories[category] ? (
                          <ChevronDown size={18} className="text-slate-400" />
                        ) : (
                          <ChevronRight size={18} className="text-slate-400" />
                        )}
                        <h3 className="font-secondary font-semibold text-slate-900">
                          {category}
                        </h3>
                        <span className="badge badge-neutral">
                          {categoryControls.length} controls
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-emerald-600 font-medium">
                          {categoryControls.filter(c => c.compliance_status === 'compliant').length} compliant
                        </span>
                      </div>
                    </button>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <div className="divide-y divide-slate-100">
                      {categoryControls.map((control) => (
                        <div 
                          key={control.id}
                          className="p-4 hover:bg-slate-50 transition-colors"
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <span className="font-mono text-sm text-blue-600 font-medium">
                                  {control.control_code}
                                </span>
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <button className="cursor-pointer">
                                      <StatusBadge status={control.compliance_status} />
                                    </button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="start">
                                    <DropdownMenuItem onClick={() => handleStatusUpdate(control.id, 'compliant')}>
                                      Mark Compliant
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleStatusUpdate(control.id, 'pending')}>
                                      Mark Pending
                                    </DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => handleStatusUpdate(control.id, 'gap')}>
                                      Mark as Gap
                                    </DropdownMenuItem>
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                              <h4 className="font-medium text-slate-900 mb-1">
                                {control.title}
                              </h4>
                              <p className="text-sm text-slate-500">
                                {truncate(control.description, 150)}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-400">
                              <FileCheck size={16} />
                              <span>{control.evidence_count} evidence</span>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CollapsibleContent>
                </div>
              </Collapsible>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={Shield}
            title="No controls found"
            description={controls.length === 0 
              ? "Load a compliance framework to get started with controls."
              : "No controls match your search criteria."
            }
            action={
              controls.length === 0 && (
                <Button
                  data-testid="empty-seed-btn"
                  onClick={handleSeedSOC2}
                  disabled={seeding}
                  className="bg-slate-900 hover:bg-slate-800 rounded-sm"
                >
                  {seeding ? 'Loading...' : 'Load SOC 2 Framework'}
                </Button>
              )
            }
          />
        )}
      </div>
    </DashboardLayout>
  );
}
